import React, { useEffect } from 'react';
import { observer } from 'mobx-react';
import { useStore } from 'app/context';
import { AgtFitnessVm, FitModel } from 'app/stores/ui/agt.fitness.ui.store';
import { ProgressRing } from 'app/components/common/ProgressRing';
import colors from 'assets/scss/colors.module.scss';
import _ from 'lodash';
import Widget from 'app/components/common/Widget';
import Panel from 'app/components/Panel';
import { FitnessTrackerAmisChart } from './FitnessTrackerAmisNow';
import { FitnessTrackerLeitfadenButton, FitnessTrackerPdfDownloadButton } from './FitnessTrackerPdfDownloadButton';
import { useHistoryWithQuery } from 'app/hooks/useHistoryWithQuery';
import routes from 'routes';
import { formatNumIgnoreNullAndUndefined, formatNumTwoDig } from 'app/utils';
import { FitnessPlanPdfDownloadButton } from '../fitnessplan/FitnessPlanPdfDownloadButton';
import { FitnessTrackerLeadNowBruttoNettoChart, FitnessTrackerLeadNowChart } from './FitnessTrackerLeadNow';
import { MazHistoryChart } from './FitnessTrackerMazHistory';

interface IFitnessTrackerGraphics {
	view: string;
	agtId: number;
	onClose: () => void;
	homeAlone?: boolean;
}
export const FitnessTrackerGraphics = observer((props: IFitnessTrackerGraphics) => {
	const { agtFitnessUiStore } = useStore();
	const { view, agtId, onClose } = props;

	const [fitVm, setFitVm] = React.useState<AgtFitnessVm | null>(null);
	useEffect(() => {
		agtFitnessUiStore.load().then(() => {
			let items = agtFitnessUiStore.items;
			if (items.length > 0) {
				items = items.filter((i) => i.agt.agtId === agtId);
			}
			setFitVm(items[0]);
		});
	}, [agtFitnessUiStore, agtId, fitVm]);

	if (!fitVm) return null;

	return (
		<>
			{props.homeAlone ? (
				<FitnessTrackerPanelContent view={view} fitVm={fitVm} />
			) : (
				<Panel classModifier="berichte-panel" title={fitVm.agt.name} header={<></>} onClose={onClose}>
					<FitnessDetailNav view={view} agtId={fitVm.agt.agtId} />
					<div className="pad-1rem">
						<FitnessTrackerPanelContent view={view} fitVm={fitVm} />
					</div>
				</Panel>
			)}
		</>
	);
});

const FitnessTrackerPanelContent = observer((props: { view: string; fitVm: AgtFitnessVm }) => {
	const { view, fitVm } = props;
	return (
		<>
			{view === 'overview' && (
				<div className="grid is-col-half align-stretch">
					<FitnessGraphicsOverview fitVm={fitVm} />
				</div>
			)}
			{view === 'gup' && (
				<div className="grid align-stretch">
					<FitnessTrackerProgressRing path="sichtbarSein" fitVm={fitVm} />
					<FitnessTrackerSocialMediaViews fitVm={fitVm} />
					<FitnessTrackerProgressRing path="socialToolbox" fitVm={fitVm} />
				</div>
			)}
			{view === 'sichtbarSein' && (
				<div className="grid align-stretch">
					<FitnessTrackerProgressRing path="sichtbarSein" fitVm={fitVm} />
				</div>
			)}
			{view === 'socialToolbox' && (
				<div className="grid align-stretch">
					<FitnessTrackerProgressRing path="socialToolbox" fitVm={fitVm} />
				</div>
			)}
			{view === 'akuma' && (
				<div className="grid align-stretch">
					<FitnessTrackerProgressRing path="akumaPrime" fitVm={fitVm} />
					<FitnessTrackerProgressRing path="akumaPlaner" fitVm={fitVm} />
				</div>
			)}
			{view === 'akumaPrime' && (
				<div className="grid align-stretch">
					<FitnessTrackerProgressRing path="akumaPrime" fitVm={fitVm} />
				</div>
			)}
			{view === 'akumaPlaner' && (
				<div className="grid align-stretch">
					<FitnessTrackerProgressRing path="akumaPlaner" fitVm={fitVm} />
				</div>
			)}
			{view === 'maz' && (
				<div className="grid align-stretch">
					<FitnessTrackerProgressRing path="immerDaSein" fitVm={fitVm} />
					<MazHistoryChart agtId={fitVm.agt.agtId} />
					<FitnessTrackerKvmWidget fitVm={fitVm} />
				</div>
			)}
			{view === 'vuinbound' && (
				<div className="grid align-stretch">
					<FitnessTrackerKvmWidget fitVm={fitVm} />
				</div>
			)}
			{view === 'amis' && (
				<div className="grid align-stretch">
					<FitnessTrackerProgressRing path="jedenKontaktNutzen" fitVm={fitVm} />
					<FitnessTrackerAmisChart fitVm={fitVm} />
				</div>
			)}
			{view === 'sts' && (
				<div className="grid align-stretch">
					<FitnessTrackerProgressRing path="sts" fitVm={fitVm} />
				</div>
			)}
			{view === 'leadnow' && (
				<div className="grid align-stretch">
					{!fitVm.leadNow && <>Keine AMIS.NOW Leads Teilnahme</>}
					{fitVm.leadNow && (
						<>
							<FitnessTrackerLeadNowBruttoNettoChart fitVm={fitVm} />
							<div className="pad-1-rem">
								<FitnessTrackerLeadNowChart fitVm={fitVm} />
							</div>
						</>
					)}
				</div>
			)}
		</>
	);
});

interface IAgtFitnessDetail {
	view: string;
}
export const AgtFitnessDetail = observer((props: IAgtFitnessDetail) => {
	const { agenturUiStore, agtFitnessUiStore } = useStore();
	const { view } = props;

	const agtId = agenturUiStore.currentAgtId;
	if (!agtId) return null;

	const [fitVm, setFitVm] = React.useState<AgtFitnessVm | null>(null);
	useEffect(() => {
		agtFitnessUiStore.load().then(() => {
			let items = agtFitnessUiStore.items;
			if (items.length > 0) {
				items = items.filter((i) => i.agt.agtId === agtId);
			}
			setFitVm(items[0]);
		});
	}, [agtFitnessUiStore, agtId, fitVm]);

	if (!fitVm) return null;
	return (
		<>
			<FitnessDetailNav view={view} agtId={fitVm.agt.agtId} />
			<div className="table-container pad-bottom-3rem">
				<FitnessTrackerPanelContent view={view} fitVm={fitVm} />
			</div>
		</>
	);
});

interface IFitnessDetailNav {
	view: string;
	agtId: number;
}
export const FitnessDetailNav = (props: IFitnessDetailNav) => {
	const { agenturUiStore } = useStore();
	const { view, agtId } = props;
	const history = useHistoryWithQuery();

	const navToFitness = (tgt: string) => {
		const currAgt = agenturUiStore.currentAgtId;
		let p = routes.BERICHTE.getPath('bestand', 'adz', 'fit', tgt, agtId.toString());
		if (currAgt) {
			p = routes.AGENTUR.getPath(currAgt.toString(), 'berichte', 'bestand', 'adz', 'fit', tgt);
		}
		history.pushWithQuery(p);
	};

	return (
		<div className="fitness-detail-nav">
			<div className="buttons">
				<button className={`button is-small is-primary ${view === 'overview' ? '' : 'is-inverted'}`} onClick={() => navToFitness('overview')}>
					Übersicht
				</button>
				<button className={`button is-small is-primary ${view === 'gup' ? '' : 'is-inverted'}`} onClick={() => navToFitness('gup')}>
					GUP
				</button>
				<button className={`button is-small is-primary ${view === 'amis' ? '' : 'is-inverted'}`} onClick={() => navToFitness('amisnowaufgaben')}>
					AMIS.NOW Aufgaben
				</button>
				<button className={`button is-small is-primary ${view === 'akuma' ? '' : 'is-inverted'}`} onClick={() => navToFitness('akuma')}>
					AKUMA
				</button>
				<button className={`button is-small is-primary ${view === 'maz' ? '' : 'is-inverted'}`} onClick={() => navToFitness('maz')}>
					mAZ
				</button>
				<button className={`button is-small is-primary ${view === 'leadnow' ? '' : 'is-inverted'}`} onClick={() => navToFitness('leadnow')}>
					AMIS.NOW Leads
				</button>
			</div>
			<div className="buttons">
				<FitnessTrackerLeitfadenButton />
				<FitnessTrackerPdfDownloadButton modifier="fake-button" agtId={agtId} />
				<FitnessPlanPdfDownloadButton agtId={agtId} />
			</div>
		</div>
	);
};
interface IFitnessGraphicsOverview {
	fitVm: AgtFitnessVm;
}
export const FitnessGraphicsOverview = observer((props: IFitnessGraphicsOverview) => {
	const { fitVm } = props;
	return (
		<>
			<FitnessTrackerProgressRing path="sichtbarSein" isOverview={true} fitVm={fitVm} />
			<FitnessTrackerProgressRing path="jedenKontaktNutzen" isOverview={true} fitVm={fitVm} />
			{fitVm.isAkumaPrimeUser === 0 ? (
				<FitnessTrackerProgressRing path="akumaPlaner" isOverview={true} fitVm={fitVm} />
			) : (
				<FitnessTrackerProgressRing path="akumaPrime" isOverview={true} fitVm={fitVm} />
			)}
			<FitnessTrackerProgressRing path="immerDaSein" isOverview={true} fitVm={fitVm} />
		</>
	);
});

interface IFitnessTrackerProgressRing {
	path: string;
	fitVm: AgtFitnessVm;
	isOverview?: boolean;
}
const FitnessTrackerProgressRing = observer((props: IFitnessTrackerProgressRing) => {
	const { path, fitVm, isOverview } = props;
	const schemaSource = isOverview ? FitnessOverviewSchema : FitnessSchema;
	const schema = schemaSource.find((s) => s.path === path);
	if (!schema) return null;

	const nodes = schema.nodes;
	const action = <span className={`tag is-${schema.color}`}>{schema.tag}</span>;

	return (
		<Widget title={schema.section} disableCollapse={true} action={action} modifier="is-paddingless">
			{path === 'sichtbarSein' && !isOverview && (
				<div className="fitness-metric-display bg-green has-border">
					<div className="label">Aktuelle Sichtbarkeit bei Google</div>
					<div className="value">{formatNumTwoDig(fitVm.gup?.rank || 0)}</div>
				</div>
			)}
			<div className={`fitness-graphics-grid ${path}`}>
				{nodes.map((node) => {
					const fm: FitModel = _.get(fitVm, node.path);
					if (!fm) return null;

					return (
						<div className="fitness-progress-ring" key={node.path}>
							<div className="title">{node.title}</div>
							{['planerAnzahlWochenNutzung', 'primeEinordnungsquote'].includes(path) && (
								<div className="akuma-tag">
									AKUMA Prime Nutzer <span className="tag is-yellow">{fitVm.isPrime ? 'JA' : 'NEIN'}</span>
								</div>
							)}
							<ProgressRing value={fm.ergebnis || 0} max={fm.max || undefined} total={fm.ziel || 100} text={`${Math.floor(fm.ze)}%`} color={colors[schema.color]} />
							<span className="subtitle">{node.subtitle(fm.ziel, fm.ergebnis)}</span>
						</div>
					);
				})}
			</div>
		</Widget>
	);
});

const FitnessTrackerSocialMediaViews = observer((props: { fitVm: AgtFitnessVm }) => {
	const { fitVm } = props;
	const views = [
		{ label: 'Facebook', value: fitVm.sm?.currFb?.views },
		{ label: 'Instagram', value: fitVm.sm?.currInsta?.views },
		{ label: 'TikTok', value: fitVm.sm?.currTiktok?.views },
		{ label: 'LinkedIn', value: fitVm.sm?.currLinkedin?.views },
	];
	const action = <span className="tag is-green">Sichten</span>;

	return (
		<Widget title="Social Media Sichten" disableCollapse={true} action={action} modifier="is-paddingless">
			<div className="grid is-col-half">
				{views.map((v) => (
					<div className="fitness-metric-display bg-green" key={v.label}>
						<div className="label">{v.label} Sichten</div>
						<div className="value">{formatNumIgnoreNullAndUndefined(v.value as number)}&nbsp;</div>
					</div>
				))}
			</div>
		</Widget>
	);
});

interface IFitnessTrackerKvmWidget {
	fitVm: AgtFitnessVm;
}
const FitnessTrackerKvmWidget = observer((props: IFitnessTrackerKvmWidget) => {
	const { fitVm } = props;

	if (!fitVm.kvm) {
		return (
			<Widget title="Immer da sein" disableCollapse={true} action={<span className="tag is-primary">KVM</span>} modifier="is-paddingless">
				<div className="pad-1rem">Keine KVM-Daten verfügbar</div>
			</Widget>
		);
	}

	const totalCalls = fitVm.kvm.vuInbound || 0;
	const averageCallsPerMonth = fitVm.kvmVuInbound3MonthAverage || 0;
	const action = <span className="tag is-primary">KVM</span>;

	return (
		<Widget title="Immer da sein" disableCollapse={true} action={action} modifier="is-paddingless">
			<div className="fitness-metric-display bg-primary">
				<div className="label">Anrufe an KVM weitergeleitet (aktuelles Jahr)</div>
				<div className="value">{formatNumTwoDig(totalCalls)}</div>
			</div>

			<div className="fitness-metric-display bg-primary">
				<div className="label">Durchschnittlich weitergeleitete Anrufe pro Monat (3 Monate)</div>
				<div className="value">{formatNumTwoDig(averageCallsPerMonth)}</div>
			</div>
		</Widget>
	);
});

interface IFitnessSchema {
	section: string;
	sectionTitle: string;
	path: string;
	color: string;
	tag: string;
	nodes: IFitnessSchemaNode[];
}
interface IFitnessSchemaNode {
	path: string;
	title: string;
	subtitle: (soll?: number, ist?: number) => React.ReactNode;
}

// Overview schema - shows summary nodes for overview/list views
export const FitnessOverviewSchema: IFitnessSchema[] = [
	{
		section: 'Sichtbar sein DigitalPersönlich',
		sectionTitle: 'Google Unternehmensprofil',
		path: 'sichtbarSein',
		color: 'green',
		tag: 'GUP',
		nodes: [
			{
				path: 'gupBewAvg',
				title: 'Google Unternehmensprofil',
				subtitle: (soll, ist) => (
					<>
						<strong>Ø-Wert der einzelnen Kennzahlen</strong> Ihres Google Unternehmensprofils
					</>
				),
			},
		],
	},
	{
		section: 'Sichtbar sein DigitalPersönlich',
		sectionTitle: 'Social Media Toolbox',
		path: 'socialToolbox',
		color: 'green',
		tag: 'GUP',
		nodes: [
			{
				path: 'fbFanAnzahl',
				title: 'Social Media Toolbox',
				subtitle: (soll, ist) => (
					<>
						<strong>{ist}</strong> von {soll} <strong>Social Media Kontakte</strong>
					</>
				),
			},
		],
	},
	{
		section: 'Jeden Kontakt nutzen',
		sectionTitle: 'AMIS.NOW Aufgaben',
		path: 'jedenKontaktNutzen',
		color: 'turquoise',
		tag: 'AMIS.NOW',
		nodes: [
			{
				path: 'amisNowQuoteRead',
				title: 'AMIS.NOW Aufgaben',
				subtitle: (soll, ist) => (
					<>
						<strong>{ist}</strong> von {soll} <strong>gesichtete Aufgaben</strong>
					</>
				),
			},
		],
	},
	{
		section: 'Jeden Kontakt nutzen',
		sectionTitle: 'Service-to-Sale',
		path: 'sts',
		color: 'turquoise',
		tag: 'S2S',
		nodes: [
			{
				path: 'stsBasicSchulungAverageFit',
				title: 'Service-to-Sale',
				subtitle: (soll, ist) => (
					<>
						<strong>{ist}%</strong> <strong>Vertrieb Schulungsfortschritt</strong>
					</>
				),
			},
		],
	},
	{
		section: 'Jeden Kunden kontaktieren',
		sectionTitle: 'AKUMA Planer',
		path: 'akumaPlaner',
		color: 'yellow',
		tag: 'AKUMA Planer',
		nodes: [
			{
				path: 'planerAnzahlWochenNutzung',
				title: 'AKUMA Planer',
				subtitle: (soll, ist) => (
					<>
						<strong>{ist}</strong> von {soll} <strong>Wochen,</strong> in denen die <strong>Übertragungsquote</strong> von bearbeiteten Kunden im AKUMA Planer zu AMIS.NOW Aufgaben{' '}
						<strong>größer 50% beträgt</strong>
					</>
				),
			},
		],
	},
	{
		section: 'Jeden Kunden kontaktieren',
		sectionTitle: 'AKUMA Prime',
		path: 'akumaPrime',
		color: 'yellow',
		tag: 'AKUMA Prime',
		nodes: [
			{
				path: 'primeEinordnungsquote',
				title: 'AKUMA Prime',
				subtitle: (soll, ist) => (
					<>
						<strong>{ist}</strong> von {soll} <strong>Kunden,</strong> die in <strong>AKUMA Prime</strong> eingeordnet und damit bearbeitet wurden
					</>
				),
			},
		],
	},
	{
		section: 'Immer da sein',
		sectionTitle: 'Meine Allianz',
		path: 'immerDaSein',
		color: 'primary',
		tag: 'mAZ',
		nodes: [
			{
				path: 'mazBestandDurchdringungsquote',
				title: 'Meine Allianz',
				subtitle: (soll, ist) => (
					<>
						<div>
							<strong>{ist}</strong> von {soll} <strong>Bestandskunden</strong> mit MeineAllianz
						</div>
						<small>Diagramm vollständig gefüllt, wenn 60% der Bestandskunden bei Meine Allianz registriert sind.</small>
					</>
				),
			},
		],
	},
	{
		section: 'Immer da sein',
		sectionTitle: 'VU Inbound (KVM)',
		path: 'vuinbound',
		color: 'primary',
		tag: 'KVM',
		nodes: [
			{
				path: 'anzahlAnrufeGesamtYtd',
				title: 'VU Inbound (KVM)',
				subtitle: (soll, ist) => (
					<>
						<strong>{ist}</strong> <strong>Anrufe gesamt</strong>
					</>
				),
			},
		],
	},
];

// Detail schema - shows detailed breakdown for individual views
export const FitnessSchema: IFitnessSchema[] = [
	{
		section: 'Sichtbar sein DigitalPersönlich',
		sectionTitle: 'Google Unternehmensprofil',
		path: 'sichtbarSein',
		color: 'green',
		tag: 'GUP',
		nodes: [
			{
				path: 'neueBewertung',
				title: 'Neue Bewertungen',
				subtitle: (soll, ist) => (
					<>
						<strong>{ist}</strong> von {soll} <strong>neue Bewertungen</strong>
					</>
				),
			},
			{
				path: 'anzBew',
				title: 'Bewertungen / Rezensionen',
				subtitle: (soll, ist) => (
					<>
						<strong>{ist}</strong> von {soll} <strong>erhaltenen Rezensionen/Bewertungen</strong>
					</>
				),
			},
			{
				path: 'bewAvg',
				title: 'Durchschnittliche Sternebewertung',
				subtitle: (soll, ist) => (
					<>
						<strong>{ist}</strong> von {soll} <strong>Ø-Sternebewertung</strong>
					</>
				),
			},
			{
				path: 'imgCount',
				title: 'Bilder / Videos',
				subtitle: (soll, ist) => (
					<>
						<strong>{ist}</strong> von {soll} <strong>hochgeladenen Bildern/Videos</strong>
					</>
				),
			},
			{
				path: 'qCount',
				title: 'Fragen & Antworten',
				subtitle: (soll, ist) => (
					<>
						<strong>{ist}</strong> von {soll} <strong>erhaltenen Fragen und Antworten</strong>
					</>
				),
			},
		],
	},
	{
		section: 'Social Media',
		sectionTitle: 'Social Media Toolbox',
		path: 'socialToolbox',
		color: 'green',
		tag: 'GUP',
		nodes: [
			{
				path: 'fbFanAnzahl',
				title: 'Facebook Fans',
				subtitle: (soll, ist) => (
					<>
						<strong>{ist}</strong> von {soll} <strong>Facebook Fans</strong>
					</>
				),
			},
			{
				path: 'instaFollower',
				title: 'Instagram Follower',
				subtitle: (soll, ist) => (
					<>
						<strong>{ist}</strong> von {soll} <strong>Instagram Follower</strong>
					</>
				),
			},
			{
				path: 'fbEigenePosts',
				title: 'Facebook Eigene Posts',
				subtitle: (soll, ist) => (
					<>
						<strong>{ist}</strong> von {soll} <strong>eigene Facebook Posts</strong>
					</>
				),
			},
			{
				path: 'instaEigenePosts',
				title: 'Instagram Eigene Posts',
				subtitle: (soll, ist) => (
					<>
						<strong>{ist}</strong> von {soll} <strong>eigene Instagram Posts</strong>
					</>
				),
			},
			{
				path: 'tikTokPosts',
				title: 'TikTok Posts',
				subtitle: (soll, ist) => (
					<>
						<strong>{ist}</strong> von {soll} <strong>TikTok Posts</strong>
					</>
				),
			},
			{
				path: 'linkedInPosts',
				title: 'LinkedIn Posts',
				subtitle: (soll, ist) => (
					<>
						<strong>{ist}</strong> von {soll} <strong>LinkedIn Posts</strong>
					</>
				),
			},
		],
	},

	{
		section: 'Jeden Kontakt nutzen',
		sectionTitle: 'AMIS.NOW Aufgaben',
		path: 'jedenKontaktNutzen',
		color: 'turquoise',
		tag: 'AMIS.NOW',
		nodes: [
			{
				path: 'amisNowQuoteRead',
				title: 'Gesichtete Aufgaben',
				subtitle: (soll, ist) => (
					<>
						<strong>{ist}</strong> von {soll} <strong>gesichtete Aufgaben</strong>
					</>
				),
			},
		],
	},
	{
		section: 'Jeden Kontakt nutzen',
		sectionTitle: 'Service-to-Sale',
		path: 'sts',
		color: 'turquoise',
		tag: 'S2S',
		nodes: [
			{
				path: 'stsBasicSchulungAverageFit',
				title: 'Vertrieb Schulungsfortschritt',
				subtitle: (soll, ist) => (
					<>
						<strong>{ist}%</strong> <strong>Vertrieb Schulungsfortschritt</strong>
					</>
				),
			},
			{
				path: 'stsMcSchulungAverageFit',
				title: 'MC Schulungsfortschritt',
				subtitle: (soll, ist) => (
					<>
						<strong>{ist}%</strong> <strong>MC Schulungsfortschritt</strong>
					</>
				),
			},
		],
	},
	{
		section: 'Jeden Kunden kontaktieren',
		sectionTitle: 'AKUMA Planer',
		path: 'akumaPlaner',
		color: 'yellow',
		tag: 'AKUMA Planer',
		nodes: [
			{
				path: 'planerAnzahlWochenNutzung',
				title: 'Wochen der Nutzung',
				subtitle: (soll, ist) => (
					<>
						<strong>{ist}</strong> von {soll} <strong>Wochen,</strong> in denen die <strong>Übertragungsquote</strong> von bearbeiteten Kunden im AKUMA Planer zu AMIS.NOW Aufgaben{' '}
						<strong>größer 50% beträgt</strong>
					</>
				),
			},
			{
				path: 'planerUebertragungquoteAmisNow',
				title: 'Übertragungsquote',
				subtitle: (soll, ist) => (
					<>
						<strong>{ist}</strong> von {soll} <strong>Kunden,</strong> die im AKUMA Planer bearbeitet und an AMIS.NOW Aufgaben übertragen wurden.
					</>
				),
			},
		],
	},
	{
		section: 'Jeden Kunden kontaktieren',
		sectionTitle: 'AKUMA Prime',
		path: 'akumaPrime',
		color: 'yellow',
		tag: 'AKUMA Prime',
		nodes: [
			{
				path: 'primeEinordnungsquote',
				title: 'Einordnungsquote',
				subtitle: (soll, ist) => (
					<>
						<strong>{ist}</strong> von {soll} <strong>Kunden,</strong> die in <strong>AKUMA Prime</strong> eingeordnet und damit bearbeitet wurden
					</>
				),
			},
			{
				path: 'primeAbwahlquote',
				title: 'Abwahlquote',
				subtitle: (soll, ist) => (
					<>
						<strong>{ist}</strong> von {soll} <strong>Kunden, die abgewählt wurden</strong>
					</>
				),
			},
			{
				path: 'primeTerminierungExtern',
				title: 'Externe Terminierungsquote',
				subtitle: (soll, ist) => (
					<>
						<strong>{ist}</strong> von {soll} <strong>Kunden,</strong> die zur externen Bearbeitung bzw. Terminierung eingeordnet wurden
					</>
				),
			},
		],
	},
	{
		section: 'Immer da sein',
		sectionTitle: 'Meine Allianz',
		path: 'immerDaSein',
		color: 'primary',
		tag: 'mAZ',
		nodes: [
			{
				path: 'mazBestandDurchdringungsquote',
				title: 'Durchdringung Bestandskunden',
				subtitle: (soll, ist) => (
					<>
						<div>
							<strong>{ist}</strong> von {soll} <strong>Bestandskunden</strong> mit MeineAllianz
						</div>
						<small>Diagramm vollständig gefüllt, wenn 60% der Bestandskunden bei Meine Allianz registriert sind.</small>
					</>
				),
			},
			{
				path: 'mazNeuDurchdringungsquote',
				title: 'Durchdringung Neukunden',
				subtitle: (soll, ist) => (
					<>
						<strong>{ist}</strong> von {soll} <strong>Neukunden</strong> mit MeineAllianz
					</>
				),
			},
		],
	},
	{
		section: 'Immer da sein',
		sectionTitle: 'VU Inbound (KVM)',
		path: 'vuinbound',
		color: 'primary',
		tag: 'KVM',
		nodes: [
			{
				path: 'anzahlAnrufeGesamtYtd',
				title: 'Anrufe durch KVM angenommen',
				subtitle: (soll, ist) => (
					<>
						<strong>{ist}</strong> <strong>Anrufe durch KVM angenommen</strong>
					</>
				),
			},
		],
	},
];
